import React, { FC, useState } from 'react'
import { Collapse, ConfigProvider, Dropdown, MenuProps, Skeleton } from 'antd'
import { DecryptedPatient } from '@icure/cardinal-sdk/model.mjs'
import { getPatientDataFormated } from '../../../../../helpers/patientDataManipulations'
import { useTranslation } from 'react-i18next'

import './index.less'
import { DropdownRow } from '../../../../common/DropdownRow'
import { deleteIcn, gearIcn, manageUserIcn } from '../../../../../assets/CustomIcons'
import { IconButton } from '../../../../common/IconButton'
import { createPortal } from 'react-dom'
import { ModalPatientForm } from '../../../PatientsDirectory/modals/ModalPatientForm'
import { ModalSharePatient } from '../../../PatientsDirectory/modals/ModalSharePatient'
import { ModalDeletePatient } from '../../../PatientsDirectory/modals/ModalDeletePatient'
import { routes } from '../../../../../navigation/Router'
import { useNavigate } from 'react-router-dom'
import { IntroHeader } from './IntroHeader'
import { RightOutlined } from '@ant-design/icons'
import { IntroContent } from './IntroContent'
import { ModalAddPatientTag } from '../../../PatientsDirectory/modals/ModalAddPatientTag'

interface IntroProps {
  patient: DecryptedPatient
  isPatientLoading?: boolean
}

const Intro: FC<IntroProps> = ({ patient, isPatientLoading }) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  const [isHeaderHovered, setHeaderHovered] = useState(false)
  const [isCollapseOpen, setCollapseOpen] = useState(true)

  const [isPatientFormModalOpen, setPatientFormModalOpen] = useState(false)
  const [isPatientToBeDeleted, setPatientToBeDeleted] = useState(false)
  const [isSharePatientModalOpen, setSharePatientModalOpen] = useState(false)
  const [isAddTagsModalOpen, setAddTagsModalOpen] = useState(false)

  const getOverflowMenu = () => {
    const items: MenuProps['items'] = [
      {
        key: 'edit_patient',
        label: <DropdownRow icon={manageUserIcn} label={t('patients.actions.edit')} />,
      },
      // We hide the share patient action for now as the feature is not ready, but we keep the code here to avoid having to rewrite it later
      // {
      //   key: 'share_patient',
      //   label: <DropdownRow icon={shareIcn} label={t('patients.actions.share')} />,
      // },
      {
        key: 'delete_patient',
        danger: true,
        label: <DropdownRow icon={deleteIcn} label={t('patients.actions.delete')} />,
      },
    ]

    const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
      domEvent.stopPropagation()

      switch (key) {
        case 'edit_patient': {
          setPatientFormModalOpen(true)
          break
        }
        case 'share_patient': {
          setSharePatientModalOpen(true)
          break
        }
        case 'delete_patient': {
          setPatientToBeDeleted(true)
          break
        }
      }
    }
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Dropdown menu={{ items, onClick }} placement="bottomRight" arrow trigger={['click']}>
          <div className={`introCollapse__expandIcon`}>
            <IconButton icon={gearIcn} withContainer={true} />
          </div>
        </Dropdown>
      </div>
    )
  }

  const IntroCollapseTheme = {
    token: {
      colorPrimary: '#084B83',
    },
    components: {
      Collapse: {
        headerBg: isCollapseOpen ? '#DDEEFF' : isHeaderHovered ? '#DDEEFF' : '#EAF4FF',
        colorBorder: isCollapseOpen ? '#DDEEFF' : isHeaderHovered ? '#D9EBFF' : '#D9EBFF',
        contentBg: 'transparent',
        borderlessContentPadding: '0',
        contentPadding: '0',

        // Height of the expand icon
        fontHeight: '32px',
      },
    },
  }

  return (
    <ConfigProvider theme={IntroCollapseTheme}>
      {/* translation hook used in overflow menu */}
      {isPatientLoading ? (
        <Skeleton avatar paragraph={{ rows: 2 }} active style={{ padding: '16px' }} />
      ) : (
        <Collapse
          className="introCollapse"
          defaultActiveKey={['intro']}
          onChange={(key) => (key.includes('intro') ? setCollapseOpen(true) : setCollapseOpen(false))}
          expandIcon={({ isActive }) => (
            <div className={`introCollapse__expandIcon ${isActive ? 'active' : ''}`}>
              <RightOutlined rotate={isActive ? 90 : 0} />
            </div>
          )}
          items={[
            {
              key: 'intro',
              label: (
                <IntroHeader
                  patient={getPatientDataFormated(patient, t, currentLanguage)}
                  handleMouseEnter={() => setHeaderHovered(true)}
                  handleMouseLeave={() => setHeaderHovered(false)}
                />
              ),
              children: <IntroContent patient={patient} onAddTag={() => setAddTagsModalOpen(true)} />,
              extra: getOverflowMenu(),
            },
          ]}
        />
      )}
      {isAddTagsModalOpen &&
        patient &&
        createPortal(<ModalAddPatientTag patient={patient} isVisible={isAddTagsModalOpen} onClose={() => setAddTagsModalOpen(false)} />, document.body)}
      {isPatientFormModalOpen &&
        patient &&
        createPortal(<ModalPatientForm mode="edit" patientToEdit={patient} isVisible={isPatientFormModalOpen} onClose={() => setPatientFormModalOpen(false)} />, document.body)}
      {isPatientToBeDeleted &&
        patient &&
        createPortal(
          <ModalDeletePatient
            isVisible={isPatientToBeDeleted}
            patient={patient}
            onClose={() => {
              setPatientToBeDeleted(false)
              navigate(routes.patients)
            }}
          />,
          document.body,
        )}
      {isSharePatientModalOpen &&
        patient &&
        createPortal(<ModalSharePatient isVisible={isSharePatientModalOpen} onClose={() => setSharePatientModalOpen(false)} patientToEdit={patient} />, document.body)}
    </ConfigProvider>
  )
}

export default Intro
